import React from 'react';
import type { Bruker } from '@klp/user/types';
import { Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import type { IContractToRegistrationProcessActivity } from '../classes/ContractToRegistrationProcessResponse';
import { mapUserRolesToCandidateGroup } from '../utils';
import { Block } from '@klp/design/index';
import {
    formatDateWithTime,
    parseSafeDate,
} from '@klp/utils/dateTimeFormatter';

type ContractFormAlertProps = {
    activity: IContractToRegistrationProcessActivity;
    currentUser?: Bruker;
};

export const ContractFormAlert: React.FC<ContractFormAlertProps> = ({
    activity,
    currentUser,
}) => {
    const { t } = useTranslation();
    const activityIsNotStarted = activity.id === null;
    if (activityIsNotStarted) {
        return (
            <Block paddingVertical="tiny">
                <Alert severity="warning">
                    <Typography>
                        {t('checkOffContract.notStartedActivity')}
                    </Typography>
                </Alert>
            </Block>
        );
    }

    const activityCompleted = activity.utfortDato !== null;
    const { isUserCandidateForActivity, requiredRoles } =
        mapUserRolesToCandidateGroup(activity, currentUser);

    if (activityCompleted) {
        return (
            <Block paddingVertical="tiny">
                <Alert severity="success">
                    <Typography>
                        {t('checkOffContract.completedActivity', {
                            name: activity.tildeltTil,
                            date: formatDateWithTime(
                                parseSafeDate(activity.utfortDato),
                            ),
                        })}
                    </Typography>
                </Alert>
            </Block>
        );
    }

    if (currentUser?.isEDAdmin) {
        return (
            <Block paddingVertical="tiny">
                <Alert severity="info">
                    <Typography>
                        {t('checkOffContract.adminInformation', {
                            role: requiredRoles,
                            assigned: activity.tildeltTil ?? '',
                        })}
                    </Typography>
                </Alert>
            </Block>
        );
    }

    if (!isUserCandidateForActivity) {
        return (
            <Block paddingVertical="tiny">
                <Alert severity="warning">
                    <Typography>
                        {t('checkOffContract.insufficientUserRole', {
                            role: requiredRoles,
                        })}
                    </Typography>
                </Alert>
            </Block>
        );
    }

    return null;
};
